import {atom} from 'jotai';
import type {ApolloError} from '@apollo/client';

/**
 * Chunk 7 (2026-09-30) — the unexplained tank-drop flag. Lifted out to
 * its own atom module (rather than folded into runTicketAtoms.ts) since
 * this isn't run-ticket state — it's computed FROM run tickets (among
 * other things) by useTankDropReconciliation.ts, the same "raw data atom
 * + derived atoms, populated by one global hook" architecture
 * runTicketAtoms.ts already established. Admin/office only — see that
 * hook's own gating; a field User never populates or reads any of this.
 */
export interface TankDropWellShare {
   wellId: string;
   wellName: string;
   /** This well's share of the tank, as a 0-100 percent — see useLeaseTankGauge.ts's own wellAllocationPercent for the identical concept, computed the same way (real report_TankAllocations data, or an equal-split guess flagged via hasValidAllocation). */
   allocationPercent: number;
   hasValidAllocation: boolean;
   /** This well's own share of dropBbls, at allocationPercent — same computeAllocatedOilProduction (tankGaugeMapping.ts) every other shared-tank display in this app already uses. */
   allocatedShareBbls: number;
}

export interface FlaggedTankDrop {
   tankId: string;
   tankNumber: string;
   leaseId: string;
   /** The date the lower (post-drop) reading was recorded. */
   dropDate: string;
   priorDate: string;
   /** The full tank-level drop, in bbls — before splitting by well share. */
   dropBbls: number;
   explained: boolean;
   /** See lib/tankDropReconciliation.ts's explainTankDrop — present only on an unexplained drop with a same-day, different-tank run ticket on the same lease. */
   mismatchNote?: string;
   /** Every well sharing this tank, each with its own allocated bbls — Duane's call, 2026-09-30: every well sharing a flagged tank shows its own share on its own WellsListPage row, not just one well for the whole tank. */
   wells: TankDropWellShare[];
}

export const flaggedTankDropsAtom = atom<FlaggedTankDrop[]>([]);
export const flaggedTankDropsLoadingAtom = atom<boolean>(true);
export const flaggedTankDropsErrorAtom = atom<ApolloError | null>(null);

/** Drives the nav/notification count — only UNEXPLAINED drops need office attention; an explained one still shows in the WellsListPage column (for visibility/audit) but was never something to act on. */
export const unexplainedTankDropCountAtom = atom((get) => get(flaggedTankDropsAtom).filter((d) => !d.explained).length);

/**
 * Keyed by wellId -> the single most-recent (by dropDate) flagged drop
 * that well appears in, whether explained or not, plus that well's own
 * share of it — WellsListPage's new column reads this directly instead
 * of re-deriving it per row. A well can appear in more than one
 * FlaggedTankDrop (2+ qualifying tanks on its lease, or drops on more
 * than one date within the window) — only the single most recent one is
 * surfaced in the list column, same "one line, not a wall of history"
 * treatment as every other WellsListPage summary column (barrel/gas/
 * water — see that page's own reportWindowByWellId).
 */
export const tankDropByWellIdAtom = atom((get) => {
   const drops = get(flaggedTankDropsAtom);
   const map = new Map<string, {drop: FlaggedTankDrop; share: TankDropWellShare}>();
   for (const drop of drops) {
      for (const share of drop.wells) {
         const existing = map.get(share.wellId);
         if (!existing || drop.dropDate > existing.drop.dropDate) {
            map.set(share.wellId, {drop, share});
         }
      }
   }
   return map;
});
